import { SB, createActions, action } from "@play-co/replicant";
const stateSchema = SB.object({
  internalTelegramAdEntryPayload: SB.map(SB.unknown()).optional()
});
const actions = createActions(stateSchema)({
  internalClearTelegramAdEntryPayload: action((state) => {
    delete state.internalTelegramAdEntryPayload;
  })
});
const telegramMessageSchema = SB.object({
  chat: SB.object({
    id: SB.int()
  }).additionalProperties(),
  from: SB.object({
    id: SB.int(),
    is_bot: SB.boolean(),
    first_name: SB.string(),
    last_name: SB.string().optional(),
    username: SB.string().optional()
  }).additionalProperties(),
  text: SB.string()
}).additionalProperties();
const telegramUpdateSchema = SB.object({
  update_id: SB.int(),
  message: telegramMessageSchema
}).additionalProperties();
function extendOnWebhookHandler(onWebhook, makeReplyMessage) {
  return async (state, webhookEvent, api) => {
    const isTelegramWebhookEvent = "platform" in webhookEvent && webhookEvent.platform === "telegram";
    if (isTelegramWebhookEvent && telegramUpdateSchema.isValid(webhookEvent)) {
      const replyMessage = makeReplyMessage(webhookEvent.message);
      const { sentReply } = await handleAdEntryMessage(
        state,
        webhookEvent.message,
        api,
        replyMessage
      );
      if (sentReply) {
        return;
      }
    }
    await onWebhook(state, webhookEvent, api);
  };
}
async function handleAdEntryMessage(state, message, api, replyMessage) {
  if (message.from.is_bot) {
    return { sentReply: false };
  }
  const [, command, args] = message.text.match(/^\/(\w+)\s*(.*)$/) ?? [];
  if (command !== "start") {
    return { sentReply: false };
  }
  const adPayload = await getAdPayload(args, api);
  state.internalTelegramAdEntryPayload = adPayload;
  api.chatbot.sendTelegramMessage({
    chatId: message.chat.id,
    message: replyMessage,
    receiverId: state.id
  });
  return { sentReply: true };
}
async function getAdPayload(commandArgs, api) {
  if (commandArgs.split("-")[0] === "ikv") {
    try {
      const payloadStr = await api.internalKvStore.get(commandArgs);
      if (payloadStr) {
        const payload = JSON.parse(payloadStr);
        const isValidPayload = payload && typeof payload === "object";
        if (!isValidPayload) {
          throw Error(
            `Unexpected payload in internal KV store key ${commandArgs}: ${payloadStr}`
          );
        }
        return payload;
      }
    } catch (error) {
      api.reportError(error instanceof Error ? error : new Error(error));
    }
  }
  return { $key: commandArgs };
}
function configureTelegramAdEntryReply(opts) {
  const { gcinstant, replicantClientPromise } = opts;
  if (gcinstant.platformID !== "telegram") {
    throw Error(`Invalid GCInstant platform: ${gcinstant.platformID}`);
  }
  gcinstant.beforeStartGameAsync(async () => {
    const replicant = tryGetExtendedReplicant(await replicantClientPromise);
    if (replicant.state.internalTelegramAdEntryPayload) {
      gcinstant.setAdEntryPayload({
        ...replicant.state.internalTelegramAdEntryPayload
      });
    }
  });
  gcinstant.beforeEntryFinal(async () => {
    const replicant = tryGetExtendedReplicant(await replicantClientPromise);
    void replicant.invoke.internalClearTelegramAdEntryPayload();
  });
}
function tryGetExtendedReplicant(replicant) {
  if (!replicant.invoke.internalClearTelegramAdEntryPayload) {
    throw Error(
      "No internalClearTelegramAdEntryPayload action found in configuration. Make sure to extend Replicant configuration with extendReplicantConfigForAnalytics"
    );
  }
  return replicant;
}
export {
  actions,
  configureTelegramAdEntryReply,
  extendOnWebhookHandler,
  stateSchema,
  telegramUpdateSchema
};
